import { starSafeInsets } from './counting';
import type { Rect } from './types';

export interface FuelZones {
  orientation: 'landscape' | 'portrait';
  rocket: Rect;
  stars: Rect;
}

export interface FuelStarSlot {
  id: number;
  cx: number;
  cy: number;
  size: number;
}

const LANDSCAPE_ROCKET_SHARE = 0.38;
const LANDSCAPE_GAP = 16;
const PORTRAIT_ROCKET_SHARE = 0.42;
const PORTRAIT_GAP = 12;
const MAX_STAR_SIZE = 120;
const STAR_FILL = 0.82;
const JITTER_SHARE = 0.8;

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function safeRect(width: number, height: number): Rect {
  const w = Math.max(0, finiteOr(width, 0));
  const h = Math.max(0, finiteOr(height, 0));
  const insets = starSafeInsets(w, h);
  const rawW = w - insets.left - insets.right;
  const rawH = h - insets.top - insets.bottom;
  if (rawW <= 0 || rawH <= 0) {
    return { x: 0, y: 0, width: Math.max(1, w), height: Math.max(1, h) };
  }
  return { x: insets.left, y: insets.top, width: rawW, height: rawH };
}

/**
 * Splits the safe rect (viewport minus HUD/caption insets) into a rocket zone and a star zone.
 * Landscape (w >= h): rocket = left 38% column, stars = remainder after a 16px gap.
 * Portrait: rocket = top 42% band, stars = remainder after a 12px gap.
 */
export function fuelZones(width: number, height: number): FuelZones {
  const w = finiteOr(width, 0);
  const h = finiteOr(height, 0);
  const safe = safeRect(w, h);
  if (w >= h) {
    const rocketW = safe.width * LANDSCAPE_ROCKET_SHARE;
    const gap = Math.min(LANDSCAPE_GAP, safe.width * 0.05);
    return {
      orientation: 'landscape',
      rocket: { x: safe.x, y: safe.y, width: rocketW, height: safe.height },
      stars: {
        x: safe.x + rocketW + gap,
        y: safe.y,
        width: safe.width - rocketW - gap,
        height: safe.height,
      },
    };
  }
  const rocketH = safe.height * PORTRAIT_ROCKET_SHARE;
  const gap = Math.min(PORTRAIT_GAP, safe.height * 0.05);
  return {
    orientation: 'portrait',
    rocket: { x: safe.x, y: safe.y, width: safe.width, height: rocketH },
    stars: {
      x: safe.x,
      y: safe.y + rocketH + gap,
      width: safe.width,
      height: safe.height - rocketH - gap,
    },
  };
}

function unit(random: () => number): number {
  const r = random();
  return Number.isFinite(r) ? Math.max(0, Math.min(0.999999999, r)) : 0.5;
}

/**
 * Places `count` (clamped 1..13) star slots on a grid inside the star zone, picking the column
 * count that maximizes the square cell size. Size = floor(cellMin * 0.82), capped at 120.
 * Jitter stays inside each cell's slack, so stars never overlap; ids are shuffled over cells.
 */
export function generateFuelLayout(
  width: number,
  height: number,
  count: number,
  random: () => number = Math.random,
): FuelStarSlot[] {
  const n = Math.min(13, Math.max(1, Math.round(finiteOr(count, 1))));
  const zone = fuelZones(width, height).stars;

  let bestCols = 1;
  let bestCell = -1;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const cell = Math.min(zone.width / cols, zone.height / rows);
    if (cell > bestCell) {
      bestCell = cell;
      bestCols = cols;
    }
  }
  const cols = bestCols;
  const rows = Math.ceil(n / cols);
  const cellW = zone.width / cols;
  const cellH = zone.height / rows;
  const cellMin = Math.min(cellW, cellH);

  const size = Math.max(1, Math.min(MAX_STAR_SIZE, Math.floor(cellMin * STAR_FILL)));
  const slackX = Math.max(0, cellW - size);
  const slackY = Math.max(0, cellH - size);

  const ids = Array.from({ length: n }, (_, i) => i);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(unit(random) * (i + 1)));
    const tmp = ids[i];
    ids[i] = ids[j];
    ids[j] = tmp;
  }

  const slots: FuelStarSlot[] = [];
  for (let k = 0; k < n; k++) {
    const r = Math.floor(k / cols);
    const c = k % cols;
    const jx = (unit(random) - 0.5) * slackX * JITTER_SHARE;
    const jy = (unit(random) - 0.5) * slackY * JITTER_SHARE;
    slots.push({
      id: ids[k],
      cx: zone.x + (c + 0.5) * cellW + jx,
      cy: zone.y + (r + 0.5) * cellH + jy,
      size,
    });
  }
  slots.sort((a, b) => a.id - b.id);
  return slots;
}
